import React, { useState, useRef } from 'react';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import { useT } from './LocaleContext';
import { useNavigate } from 'react-router-dom';
import { GlobalStyle, DropdownItem, MenuButton, DropdownMenu } from './styles/GlobalStyles';
import { useOutsideClick, validateEmail, getErrorMessage } from './common';
import { authAPI } from './api';
import { 
  LoginContainer, 
  Title, 
  Form,
  Input, 
  Button, 
  Message,
  ForgotPasswordLink
} from './styles/LoginPageStyles';


const LoginPage = () => {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [is2FA, setIs2FA] = useState(false);
  const [message, setMessage] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useOutsideClick(dropdownRef, () => setMenuOpen(false));
  const toggleMenu = () => {setMenuOpen((prev) => !prev);};

  const handleLogin = async () => {
    if (!email || !password) {
      setMessage({ text: t('validation.emailAndPasswordRequired'), success: false });
      return;
    }

    const emailValidation = validateEmail(email.trim());
    if (!emailValidation.valid) {
      setMessage({ text: emailValidation.message, success: false });
      return;
    }

    try {
      const response = await authAPI.login({ email: email.trim(), password });
      if (response.data.temp_token) {
        setIs2FA(true);
        setMessage({ text: t('login.enterAuthenticator'), success: false });
      } else if (response.data.success) {
        window.dispatchEvent(new Event('userLoggedIn'));
        setMessage({ text: t('login.successRedirect'), success: true });
        setTimeout(() => {
          navigate('/chats');
        }, 2000);
      } else {
        setMessage({ text: t('login.loginFailed'), success: false });
      }
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data || {};
      const msg = String(data.msg || data.detail || '').toLowerCase();
      const notActivated =
        status === 403 &&
        (data.error_code === 2003 || msg.includes('not activated'));

      if (notActivated) {
        navigate('/activate', {
          state: {
            email: email.trim(),
            fromRegistration: false,
            fromLogin: true,
          },
        });
        return;
      }

      const errorMessage = getErrorMessage(error, t('login.signInError'));
      setMessage({ text: errorMessage, success: false });
    }
  };

  const handle2FAVerify = async () => {
    if (!code) {
      setMessage({ text: t('login.enter2faCode'), success: false });
      return;
    }

    try {
      // Временный токен уже установлен в куки при первом логине
      // Теперь просто отправляем код 2FA, токен будет взят из кук автоматически
      const response = await authAPI.twoFactorVerify(code.trim());
      
      // После успешной верификации постоянные токены установлены в HttpOnly куки
      if (response.data.success) {
        window.dispatchEvent(new Event('userLoggedIn'));
        setMessage({ text: t('login.twoFaSuccess'), success: true });
        // Даем время браузеру сохранить куки перед редиректом
        setTimeout(() => {
          navigate('/chats');
        }, 500);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, t('login.verify2faError'));
      setMessage({ text: errorMessage, success: false });
    }
  };

  return (
    <LoginContainer>
      <GlobalStyle />
      {message && <Message success={message.success}>{message.text}</Message>}
      <MenuButton onClick={toggleMenu} $open={menuOpen}>≡</MenuButton>
      <DropdownMenu ref={dropdownRef} open={menuOpen}>
        <DropdownItem to="/">{t('nav.home')}</DropdownItem>
        <DropdownItem to="/register">{t('nav.register')}</DropdownItem>
        <ThemeToggle />
        <LanguageToggle />
      </DropdownMenu>
      
      {!is2FA ? (
        <>
          <Title>{t('login.title')}</Title>
          <Form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
            <Input
              type="text"
              value={email}
              maxLength={254}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value.trim())}
              placeholder={t('common.email')}
            />
            <Input
              type="password"
              value={password}
              maxLength={128}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && email && password) {
                  e.preventDefault();
                  handleLogin();
                }
              }}
              placeholder={t('common.password')}
            />
            <Button as="button" type="submit">{t('login.signIn')}</Button>
          </Form>
          <ForgotPasswordLink to="/password-forgot">{t('login.forgotPassword')}</ForgotPasswordLink>
        </>
      ) : (
        <>
          <Title>{t('login.twoFaTitle')}</Title>
          <Form onSubmit={(e) => { e.preventDefault(); handle2FAVerify(); }}>
            <Input
              type="text"
              value={code}
              maxLength={10}
              autoComplete="one-time-code"
              inputMode="numeric"
              onChange={(e) => setCode(e.target.value.trim())}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code) {
                  e.preventDefault();
                  handle2FAVerify();
                }
              }}
              placeholder={t('common.code')}
            />
            <Button as="button" type="submit">{t('common.submit')}</Button>
          </Form>
        </>
      )}
    </LoginContainer>
  );
};

export default LoginPage;
